import {
  delay,
  put,
  select,
  call,
  takeEvery,
  all,
  fork,
  take
} from "redux-saga/effects";
import * as R from "ramda";

import { getCredentials } from "redux/modules/user/selectors";
import { registerError } from "redux/modules/errors/actions";

import { actions, getters } from "SendEmailModal/model";
import { getIsCurrentStateValid } from "SendEmailModal/selectors";
import Api from "SendEmailModal/api";
import { findDOMNode } from "react-dom";

import { orgId as getOrgId } from "redux/modules/organization/selectors";
import { eventId as getEventId } from "redux/modules/event/selectors";
import { getMessage } from "./selectors";

import { MESSAGE_WYSIWYG_EDITOR_FIELD_ID, MODES } from "./constants";

import { defaultInstanceId } from "redux-mvc";

import { actions as FormActions } from "ui-kit/Form/model";
import { getParticularContactsSelectedIds } from "SendEmailModal/selectors";
import * as STANDARD_MODULE_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-modules";

const getPayload = (state, send = false) => {
  const moduleId = getters.moduleId(state);
  const mode = getters.mode(state);
  const activeStepIndex = getters.activeStepIndex(state);
  const selectedOptions = getters.selectedOptions(state);
  return {
    mode,
    emailType: getters.emailType(state),
    moduleId,
    orgId: getOrgId(state),
    eventId: getEventId(state),
    message: getMessage(state),
    recordIds: getters.records(state),
    sourceRecordId: getters.sourceRecordId(state),
    sourceFormId: getters.sourceFormId(state),
    sourceOrderId: getters.sourceOrderId(state),
    options: {
      ...(mode === MODES.EMAIL &&
        activeStepIndex > 1 &&
        selectedOptions === "select-individual-contacts" &&
        STANDARD_MODULE_IDS.sessions.id === moduleId && {
          contactIds: getParticularContactsSelectedIds(state) || []
        }),
      groupByRecipient: getters.groupByRecipient(state),
      recipientKeys: selectedOptions ? [selectedOptions] : [],
      send,
      subject: getters.subject(state),
      replyTo: getters.replyTo(state),

      // @NOTE: Passing empty array for attachments until we turn them back on
      attachments: []
      // attachments: getters.attachments(state)
    }
  };
};

const handleNextStep = function*() {
  const state = yield select(R.identity);
  const isValid = yield select(getIsCurrentStateValid);
  const activeStepIndex = yield select(getters.activeStepIndex);
  const bodyRef = yield select(getters.bodyRef);
  const emailType = yield select(getters.emailType);
  const invoiceDueDate = yield select(getters.invoiceDueDate);
  const invoiceNotes = yield select(getters.invoiceNotes);
  const records = yield select(getters.records);

  const credentials = yield select(getCredentials);
  const res = {};
  if (isValid) {
    if (activeStepIndex === 0) {
      yield put(actions.setPreview(null));
      yield put(actions.setGeneratingPreviews(true));

      const initialResult = yield call(
        Api.post,
        credentials,
        getPayload(state, false)
      );

      yield delay(300);

      let progressResult = yield call(Api.getStatus, credentials, {
        jobId: initialResult.payload.job_id
      });

      while (
        ["pending", "processing"].includes(progressResult.payload.status)
      ) {
        yield delay(1000);
        progressResult = yield call(Api.getStatus, credentials, {
          jobId: initialResult.payload.job_id
        });
      }

      if (progressResult.payload.status === "error") {
        yield all([
          put(
            registerError([
              {
                system: "An error occurred generating previews",
                user: "An error occurred generating previews"
              }
            ])
          ),
          put(actions.setGeneratingPreviews(false))
        ]);
        return false;
      }

      res.generatingPreviews = false;
      res.messagePreviews = progressResult.payload.payload.previews;
    } else if (activeStepIndex === 2) {
      yield put(actions.setSendingEmails(true));

      if (emailType === "invoice") {
        yield all(
          records.map(orderId =>
            call(Api.updateOrder, credentials, {
              orderId,
              eventId: getEventId(state),
              order: {
                invoiceDueDate,
                invoiceNotes
              }
            })
          )
        );
      }

      const initialResult = yield call(
        Api.post,
        credentials,
        getPayload(state, true)
      );

      yield delay(300);

      let progressResult = yield call(Api.getStatus, credentials, {
        jobId: initialResult.payload.job_id
      });

      while (
        ["pending", "processing"].includes(progressResult.payload.status)
      ) {
        yield delay(1000);
        progressResult = yield call(Api.getStatus, credentials, {
          jobId: initialResult.payload.job_id
        });
      }

      if (progressResult.payload.status === "error") {
        yield all([
          put(
            registerError([
              {
                system: "An error occurred sending emails",
                user: "An error occurred sending emails"
              }
            ])
          ),
          put(actions.setSendingEmails(false))
        ]);
        return false;
      }

      res.sendingEmails = false;
    }

    findDOMNode(bodyRef).scrollTop = 0;

    yield put(
      actions.handleResponse({
        preview: null,
        activeStepIndex: activeStepIndex + 1,
        generatingPreviews: false,
        sendingEmails: false,
        ...res
      })
    );
  }

  return true;
};

const refreshPreviews = function*() {
  const state = yield select(R.identity);
  const credentials = yield select(getCredentials);

  yield put(actions.setPreview(null));
  yield put(actions.setGeneratingPreviews(true));

  const initialResult = yield call(
    Api.post,
    credentials,
    getPayload(state, false)
  );

  yield delay(300);

  let progressResult = yield call(Api.getStatus, credentials, {
    jobId: initialResult.payload.job_id
  });

  while (["pending", "processing"].includes(progressResult.payload.status)) {
    yield delay(1000);
    progressResult = yield call(Api.getStatus, credentials, {
      jobId: initialResult.payload.job_id
    });
  }

  if (progressResult.payload.status === "error") {
    yield all([
      put(
        registerError([
          {
            system: "An error occurred refreshing previews",
            user: "An error occurred refreshing previews"
          }
        ])
      ),
      put(actions.setGeneratingPreviews(false))
    ]);
    return false;
  }

  const messagePreviews = progressResult.payload.payload.previews;

  yield put(
    actions.handleResponse({
      preview: null,
      generatingPreviews: false,
      messagePreviews
    })
  );

  return true;
};

const getPreview = function*({
  payload: { parentRecordId, childRecordId, arrayOfContacts }
}) {
  try {
    const state = yield select(R.identity);
    const credentials = yield select(getCredentials);
    yield put(actions.setGeneratingPreviews(true));

    const { payload: previewPayload } = yield call(
      Api.getPreview,
      credentials,
      {
        ...getPayload(state, false),
        parentRecordId,
        childRecordId
      }
    );

    const meta = R.pathOr({}, ["meta"])(previewPayload);
    const { id, email, name, phone } = meta;

    yield put(
      actions.setPreview({
        id,
        subject: R.pathOr({}, ["variables", "subject"])(previewPayload),
        phone,
        email,
        name,
        content: R.pathOr({}, ["preview"])(previewPayload)
      })
    );
    if (arrayOfContacts && arrayOfContacts.length > 0) {
      let multiplePreview = [];
      for (let i = 0; i < arrayOfContacts.length; i++) {
        const {
          parentRecordId: parentRecordIdContact,
          childRecordId: childRecordIdContact
        } = arrayOfContacts[i];
        if (parentRecordIdContact !== parentRecordId) {
          const response = yield call(Api.getPreview, credentials, {
            ...getPayload(state, false),
            parentRecordId: parentRecordIdContact,
            childRecordId: childRecordIdContact
          });
          const meta = R.pathOr({}, ["payload", "meta"])(response);
          const { id, email, name, phone } = meta;
          multiplePreview.push({
            id,
            subject: R.pathOr({}, ["payload", "variables", "subject"])(
              response
            ),
            phone,
            email,
            name,
            content: R.pathOr({}, ["payload", "preview"])(response)
          });
        }
      }
      yield put(actions.setMultiplePreview(multiplePreview));
    }
    yield put(actions.setGeneratingPreviews(false));
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred getting preview"
        }
      ])
    );
  }

  return true;
};

const handleSelectParticularContact = function*({ payload: { id } }) {
  const preview = yield select(getters.preview);
  if (preview?.id === id) {
    yield all([
      put(actions.setPreview(null)),
      put(actions.setMultiplePreview(null))
    ]);
  }
  const particularContactsSelected = yield select(
    getters.particularContactsSelected
  );

  const newContactSelected = R.map(contact => {
    if (id === R.prop("id", contact)) {
      return { ...contact, selected: !contact.selected };
    }
    return contact;
  }, particularContactsSelected);

  yield put(actions.setParticularContactsSelected(newContactSelected));

  const contactsSelected = R.filter(
    R.propEq("selected", true),
    newContactSelected
  );

  if (contactsSelected.length === 1 && !preview) {
    const messagePreviews = yield select(getters.messagePreviews);
    const managePreview = R.compose(
      R.map(c => ({
        parentRecordId: R.path(["meta", "recordIds", 0])(c),
        childRecordId: R.path(["meta", "id"])(c)
      })),
      R.filter(
        recipient =>
          R.pathOr("", ["meta", "id"])(recipient) === contactsSelected[0].id
      )
    )(messagePreviews);
    yield put(
      actions.getPreview({
        parentRecordId: R.prop("parentRecordId", managePreview[0]),
        childRecordId: R.prop("childRecordId", managePreview[0]),
        arrayOfContacts: managePreview
      })
    );
  }

  return true;
};

const selectAllHandler = function*() {
  const generatingPreviews = yield select(getters.generatingPreviews);
  if (generatingPreviews) return true;
  const particularContactsSelected = yield select(
    getters.particularContactsSelected
  );
  const lengthOfSelected = R.compose(
    R.length,
    R.filter(R.propEq("selected", true))
  )(particularContactsSelected);

  if (
    lengthOfSelected === particularContactsSelected.length &&
    lengthOfSelected > 0
  ) {
    yield all([
      put(actions.setPreview(null)),
      put(actions.setMultiplePreview(null))
    ]);
  }
  yield put(
    actions.setParticularContactsSelected(
      R.compose(
        R.map(contact =>
          lengthOfSelected === particularContactsSelected.length
            ? { ...contact, selected: false }
            : { ...contact, selected: true }
        )
      )(particularContactsSelected)
    )
  );

  if (lengthOfSelected !== particularContactsSelected.length) {
    const messagePreviews = yield select(getters.messagePreviews);
    const managePreview = R.compose(
      R.map(c => ({
        parentRecordId: R.path(["meta", "recordIds", 0])(c),
        childRecordId: R.path(["meta", "id"])(c)
      })),
      R.filter(
        recipient =>
          R.pathOr("", ["meta", "id"])(recipient) ===
          particularContactsSelected[0].id
      )
    )(messagePreviews);
    yield put(
      actions.getPreview({
        parentRecordId: R.prop("parentRecordId", managePreview[0]),
        childRecordId: R.prop("childRecordId", managePreview[0]),
        arrayOfContacts: managePreview
      })
    );
  }

  return true;
};

const updateMessage = function*({ payload }) {
  const currentMesage = yield select(getMessage);
  const updatedMessage = `${currentMesage} ${payload.message}`;
  yield put(
    FormActions.setFieldValue(updatedMessage, {
      meta: {
        fieldId: MESSAGE_WYSIWYG_EDITOR_FIELD_ID
      }
    })
  );
};

const handleBackStep = function*() {
  const currentMesage = yield select(getMessage);
  const activeStepIndex = yield select(getters.activeStepIndex);

  if (activeStepIndex === 0) {
    // we delay a bit to let the Editor set before populating the WYSIWYG editor
    yield delay(500);
    yield put(
      FormActions.setFieldValue(currentMesage, {
        meta: {
          fieldId: MESSAGE_WYSIWYG_EDITOR_FIELD_ID
        }
      })
    );
  }
};

const fieldUpdateSMS = function*() {
  const modeType = yield select(getters.mode);
  if (modeType === MODES.SMS) {
    return true;
  } else {
    return false;
  }
};

const watchSMSEditorUpdate = function*() {
  for (;;) {
    const {
      meta: { instanceId, fieldId }
    } = yield take(FormActions.setFieldValue.type);
    if (
      instanceId === defaultInstanceId &&
      fieldId === MESSAGE_WYSIWYG_EDITOR_FIELD_ID
    ) {
      yield call(fieldUpdateSMS, {
        id: instanceId,
        fieldId
      });
    }
  }
};

const watchHandleNextStep = function*() {
  yield takeEvery(actions.handleNextStep.type, handleNextStep);
};

const watchRefreshPreviews = function*() {
  yield takeEvery(actions.refreshPreviews.type, refreshPreviews);
};

const watchGetPreview = function*() {
  yield takeEvery(actions.getPreview.type, getPreview);
};
const watchHandleSelectParticularContact = function*() {
  yield takeEvery(
    actions.handleSelectParticularContact.type,
    handleSelectParticularContact
  );
};

const watchHandleSelectAllParticularContacts = function*() {
  yield takeEvery(actions.selectAllHandler.type, selectAllHandler);
};

const watchUpdateMessage = function*() {
  yield takeEvery(actions.updateMessage.type, updateMessage);
};

const watchHandleBackStep = function*() {
  yield takeEvery(actions.handleBackStep.type, handleBackStep);
};

const rootSaga = function*() {
  yield all([
    fork(watchUpdateMessage),
    fork(watchHandleNextStep),
    fork(watchRefreshPreviews),
    fork(watchGetPreview),
    fork(watchHandleSelectParticularContact),
    fork(watchHandleSelectAllParticularContacts),
    fork(watchHandleBackStep),
    fork(watchSMSEditorUpdate)
  ]);
};

export default rootSaga;
